import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";
import { trackNavigation } from "./lib/appHistory.js";
import { useSession } from "./lib/auth-client.js";
import { capturePageview, identifyUser, initPostHog, resetUser } from "./lib/posthog.js";
import { TRPCProvider } from "./lib/providers.js";
import { initSentry, resetSentryUser, setSentryUser } from "./lib/sentry.js";
import { routeTree } from "./routeTree.gen";
import "./index.css";

// Init observability
initSentry();
initPostHog();

// Create the router instance
const router = createRouter({
  routeTree,
  scrollRestoration: true,
});

// Register the router for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function PostHogIdentifier() {
  const { data: session } = useSession();
  const user = session?.user;
  const isSignedIn = !!user;

  useEffect(() => {
    if (isSignedIn && user) {
      identifyUser(user.id, {
        email: user.email ?? "",
        username: user.name ?? "",
      });
      setSentryUser(user.id, {
        email: user.email ?? "",
        username: user.name ?? "",
      });
    } else if (!isSignedIn) {
      resetUser();
      resetSentryUser();
    }
  }, [isSignedIn, user]);

  return null;
}

// Track pageviews on route changes
router.subscribe("onLoad", () => {
  capturePageview();
  trackNavigation();
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <TRPCProvider>
      <PostHogIdentifier />
      <RouterProvider router={router} />
      <Toaster
        theme="dark"
        position="bottom-right"
        offset="16px"
        toastOptions={{
          style: {
            background: "var(--card)",
            border: "1px solid var(--border)",
            color: "var(--card-foreground)",
          },
        }}
      />
    </TRPCProvider>
  </StrictMode>,
);
